/**
 * Financial Aid Value Objects: Assets Detail
 *
 * Sections 12-14: Business Ownership, Real Estate, Vehicles
 *
 * @module financial-aid-domain/value-objects/financial-aid/assets-detail
 */
import { z } from 'zod';
import { AddressSchema } from '@triedge/domain-core';

// ============================================================================
// Section 12: Business Ownership
// ============================================================================

export const BusinessOwnershipSchema = z.object({
  ownsBusinesses: z.boolean().default(false),
  businesses: z.array(z.object({
    businessName: z.string().min(1),
    ownershipPercentage: z.number().min(0).max(100),
    businessType: z.enum(['sole_proprietorship', 'partnership', 'llc', 's_corp', 'c_corp', 'other']),
    annualRevenue: z.number().nonnegative().nullable().optional(),
    annualProfit: z.number().nullable().optional(), // Can be negative
    numberOfEmployees: z.number().int().nonnegative().nullable().optional(),
  })).default([]),
});
export type BusinessOwnership = z.infer<typeof BusinessOwnershipSchema>;

// ============================================================================
// Section 13: Real Estate
// ============================================================================

export const RealEstatePropertySchema = z.object({
  propertyType: z.enum(['primary_residence', 'vacation_home', 'rental_property', 'commercial', 'land', 'other']),
  address: AddressSchema.nullable().optional(),
  currentValue: z.number().nonnegative(),
  mortgageBalance: z.number().nonnegative().default(0),
  equity: z.number(), // Computed: currentValue - mortgageBalance
  monthlyMortgage: z.number().nonnegative().default(0),
  rentalIncome: z.number().nonnegative().default(0), // If rental property
});
export type RealEstateProperty = z.infer<typeof RealEstatePropertySchema>;

export const RealEstateSchema = z.object({
  ownsRealEstate: z.boolean().default(false),
  properties: z.array(RealEstatePropertySchema).default([]),
  totalRealEstateValue: z.number().nonnegative().default(0),
  totalRealEstateEquity: z.number().default(0),
});
export type RealEstate = z.infer<typeof RealEstateSchema>;

// ============================================================================
// Section 14: Vehicles
// ============================================================================

export const VehicleSchema = z.object({
  make: z.string().min(1),
  model: z.string().min(1),
  year: z.number().int().min(1900).max(2100),
  estimatedValue: z.number().nonnegative(),
  loanBalance: z.number().nonnegative().default(0),
  monthlyPayment: z.number().nonnegative().default(0),
  isLeased: z.boolean().default(false),
});
export type Vehicle = z.infer<typeof VehicleSchema>;

export const VehiclesSchema = z.object({
  vehicles: z.array(VehicleSchema).default([]),
  totalVehicleValue: z.number().nonnegative().default(0),
  totalVehicleLoans: z.number().nonnegative().default(0),
});
export type Vehicles = z.infer<typeof VehiclesSchema>;
